import type { AppCoordinator } from '../app/AppCoordinator';
import type { MediaCoordinator } from '../media/MediaCoordinator';
import { DisplaySourceManager } from '../media/managers/DisplaySourceManager';
import { AudioManager } from '../media/managers/AudioManager';
import { DisplayOutputManager } from '../media/managers/DisplayOutputManager';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type { IReplicationRouter } from '../ReplicationRouter';
import type { IIpcService } from '../../services/ipc/IIpcService';

export interface BeaconMediaManagerDeps {
  ipcService: IIpcService;
  app: Pick<AppCoordinator, 'getHostAuthId'>;
  media: { getDeviceConfig(): Pick<ReturnType<MediaCoordinator['getDeviceConfig']>,
    'getCameras' | 'getDesktopAudioSources' | 'getMicrophones'>;
    events: Pick<MediaCoordinator['events'], 'deviceListChanged'> };
  session: { events: Pick<SessionCoordinator['events'], 'statusChanged'> };
  replication: IReplicationRouter;
  shadowcams: { readonly currentState: { active: string[] } };
}

/** Composes Beacon media controls with injected device, replication and stream sources. */
export class BeaconMediaManagerInitializer {
  public constructor(private readonly deps: BeaconMediaManagerDeps) {}

  public initialize(): { displaySourceManager: DisplaySourceManager; audioManager: AudioManager;
    displayOutputManager: DisplayOutputManager } {
    return { displaySourceManager: this.initializeDisplaySource(), audioManager: this.initializeAudio(),
      displayOutputManager: this.initializeDisplayOutput() };
  }

  private initializeDisplaySource(): DisplaySourceManager {
    const manager = new DisplaySourceManager({ ipcService: this.deps.ipcService,
      getHostAuthId: () => this.deps.app.getHostAuthId(),
      getActiveShadowcams: () => this.deps.shadowcams.currentState.active });
    manager.registerReplicationHandler(this.deps.replication);
    const cameras = this.deps.media.getDeviceConfig().getCameras();
    if (cameras.length > 0) manager.onCamerasChanged(cameras);
    this.deps.media.events.deviceListChanged.addListener(() => {
      manager.onCamerasChanged(this.deps.media.getDeviceConfig().getCameras());
    });
    return manager;
  }

  private initializeAudio(): AudioManager {
    const manager = new AudioManager({ ipcService: this.deps.ipcService,
      getHostAuthId: () => this.deps.app.getHostAuthId() });
    manager.registerReplicationHandler(this.deps.replication);
    const devices = this.deps.media.getDeviceConfig();
    manager.updateDeviceLists(devices.getDesktopAudioSources(), devices.getMicrophones());
    this.deps.media.events.deviceListChanged.addListener(() => {
      const current = this.deps.media.getDeviceConfig();
      manager.updateDeviceLists(current.getDesktopAudioSources(), current.getMicrophones());
    });
    // Capture stays at unity: the Beacon volume slider has no second gain bridge
    // into MediaCoordinator's capture pipeline.
    return manager;
  }

  private initializeDisplayOutput(): DisplayOutputManager {
    const manager = new DisplayOutputManager({ ipcService: this.deps.ipcService,
      getActiveShadowcams: () => this.deps.shadowcams.currentState.active });
    manager.registerReplicationHandler(this.deps.replication);
    this.deps.session.events.statusChanged.addListener(event => {
      const isLive = event.status === 'hosting';
      manager.updateStreamStatus(isLive, isLive ? Date.now() : 0);
    });
    return manager;
  }
}
